import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Eye, EyeOff, Loader2, Upload, Image, Link as LinkIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPromos = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [buttonLink, setButtonLink] = useState('');
  const fileInputRef = useRef(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchPromos = async () => {
    try {
      const response = await axios.get(`${API}/admin/promos`, { headers: getAuthHeader() });
      setPromos(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des promotions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromos();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop grande (max 5MB)');
      return;
    }

    setUploading(true);
    setShowAddModal(false);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      });

      const uploadedUrl = response.data.url || response.data.image_url;
      if (!uploadedUrl) {
        throw new Error('No URL returned');
      }
      
      await createPromo(uploadedUrl, '');
      toast.success('Promotion ajoutée!');
    } catch (error) {
      console.error('Upload error:', error?.response?.data || error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploading(false);
      await fetchPromos();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUrlSubmit = async () => {
    if (!imageUrl.trim()) {
      toast.error('Veuillez entrer une URL');
      return;
    }

    setUploading(true);
    try {
      await createPromo(imageUrl.trim(), buttonLink.trim());
      toast.success('Promotion ajoutée!');
      setImageUrl('');
      setButtonLink('');
      setShowAddModal(false);
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setUploading(false);
    }
  };

  const createPromo = async (url, link) => {
    const payload = {
      title: 'Promo',
      subtitle: '',
      button_text: '',
      button_link: link || '',
      promo_type: 'default',
      image_url: url,
      is_active: true,
      position: promos.length,
    };

    await axios.post(`${API}/admin/promos`, payload, { headers: getAuthHeader() });
  };

  const handleDelete = async (promo) => {
    if (!window.confirm('Supprimer cette promotion?')) return;
    
    try {
      await axios.delete(`${API}/admin/promos/${promo.id}`, { headers: getAuthHeader() });
      toast.success('Promotion supprimée');
      fetchPromos();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(`${API}/admin/promos/${promo.id}`, {
        ...promo,
        is_active: !promo.is_active,
      }, { headers: getAuthHeader() });
      toast.success(promo.is_active ? 'Promotion désactivée' : 'Promotion activée');
      fetchPromos();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateLink = async (promo, newLink) => {
    try {
      await axios.put(`${API}/admin/promos/${promo.id}`, {
        ...promo,
        button_link: newLink,
      }, { headers: getAuthHeader() });
      fetchPromos();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Publicités</h1>
          <p className="text-slate-500 text-sm mt-1">
            Ajoutez vos visuels promotionnels pour la page d'inventaire
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          Ajouter
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 mb-2">Taille recommandée</h3>
        <p className="text-sm text-blue-700">
          <strong>600 x 400 pixels</strong> (ratio 3:2) • Format PNG ou WebP • Max 5MB
        </p>
        <p className="text-sm text-blue-600 mt-2">
          Les publicités s'affichent dans l'ordre suivant (répété):
        </p>
        <ul className="text-sm text-blue-600 mt-1 ml-4 list-disc">
          <li><strong>Pub 1, 4, 7...</strong> → Position droite</li>
          <li><strong>Pub 2, 5, 8...</strong> → Position gauche</li>
          <li><strong>Pub 3, 6, 9...</strong> → Position centre</li>
        </ul>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-800">Ajouter une publicité</h2>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Upload File */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-3">Télécharger une image</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="promo-upload"
                />
                <label
                  htmlFor="promo-upload"
                  className={`flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-8 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {uploading ? (
                    <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-slate-400 mb-2" />
                      <span className="text-sm text-slate-600">Cliquez pour sélectionner</span>
                      <span className="text-xs text-slate-400 mt-1">PNG, JPG, WebP (max 5MB)</span>
                    </>
                  )}
                </label>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 border-t border-slate-200"></div>
                <span className="text-sm text-slate-400">ou</span>
                <div className="flex-1 border-t border-slate-200"></div>
              </div>

              {/* URL Input */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-3">Utiliser une URL</p>
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://exemple.com/image.png"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 mb-3"
                />
                <input
                  type="text"
                  value={buttonLink}
                  onChange={(e) => setButtonLink(e.target.value)}
                  placeholder="Lien au clic (optionnel): /credit ou https://..."
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-slate-800 text-sm"
                />
                <button
                  onClick={handleUrlSubmit}
                  disabled={!imageUrl.trim() || uploading}
                  className="w-full mt-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Ajouter
                </button>
              </div>
            </div>

            <div className="p-4 border-t border-slate-200">
              <button
                onClick={() => { setShowAddModal(false); setImageUrl(''); setButtonLink(''); }}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promos Grid */}
      {promos.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Image className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Aucune publicité</h3>
          <p className="text-slate-500 mb-4">
            Ajoutez vos visuels promotionnels pour les afficher sur la page d'inventaire
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Ajouter une publicité
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {promos.map((promo, index) => {
            const positions = ['Droite', 'Gauche', 'Centre'];
            const positionLabel = positions[index % 3];
            
            return (
            <div
              key={promo.id}
              className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${
                !promo.is_active ? 'opacity-50' : ''
              }`}
            >
              {/* Slot Label */}
              <div className="px-3 py-2 text-xs font-semibold bg-blue-50 text-blue-700">
                Publicité {index + 1} • Position {positionLabel}
              </div>
              
              {/* Image Preview */}
              <div className="bg-slate-100 relative" style={{ aspectRatio: '3/2' }}>
                <img
                  src={promo.image_url}
                  alt="Promo"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200"><rect fill="%23f1f5f9" width="400" height="200"/><text fill="%2394a3b8" x="50%" y="50%" text-anchor="middle" dy=".3em" font-family="sans-serif">Image non disponible</text></svg>';
                  }}
                />
                {!promo.is_active && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <span className="bg-white/90 text-slate-700 px-3 py-1 rounded-full text-sm font-medium">
                      Désactivée
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {/* Status Badge */}
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    promo.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {promo.is_active ? 'Active' : 'Inactive'}
                  </span>
                  
                  {/* Link indicator */}
                  {promo.button_link && (
                    <span className="text-xs text-blue-600 flex items-center gap-1">
                      <LinkIcon className="h-3 w-3" />
                      {promo.button_link.length > 20 ? promo.button_link.substring(0, 20) + '...' : promo.button_link}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleActive(promo)}
                    className={`p-2 rounded-lg transition-colors ${
                      promo.is_active 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-slate-400 hover:bg-slate-50'
                    }`}
                    title={promo.is_active ? 'Désactiver' : 'Activer'}
                  >
                    {promo.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => handleDelete(promo)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
          })}
        </div>
      )}

      {/* Stats */}
      {promos.length > 0 && (
        <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-between text-sm">
          <span className="text-slate-600">
            {promos.filter(p => p.is_active).length} publicité(s) active(s) sur {promos.length}
          </span>
          <span className="text-slate-500">
            Pattern: droite → gauche → centre (répété)
          </span>
        </div>
      )}
    </div>
  );
};

export default AdminPromos;
